/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * Live highlights feed — polls /sessions/{sid}/highlights every 10s and shows the
 * last 5 bursts. Lets the streamer manually mark a moment ("Clip this").
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FireIcon, FilmSlateIcon, LightningIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const kindColor = (kind) => {
  if (kind === "funny") return { c: "#FFCA28", icon: <LightningIcon size={12} weight="fill" /> };
  if (kind === "manual") return { c: "#00E5FF", icon: <FilmSlateIcon size={12} weight="fill" /> };
  return { c: "#FF007F", icon: <FireIcon size={12} weight="fill" /> };
};

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  } catch (e) { return ""; }
};

const HighlightsFeed = ({ sessionId }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get(`/sessions/${sessionId}/highlights`);
      setItems(r.data.highlights || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    if (!sessionId) return;
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [sessionId]); // eslint-disable-line

  const clipThis = async () => {
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/highlights/mark`, { session_id: sessionId, note: "" });
      toast.success("Clipped — highlight saved 🎬");
      refresh();
    } catch (e) {
      toast.error("Clip failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-4" data-testid="highlights-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FireIcon size={14} color="#FF007F" weight="fill" />
          <div className="font-display font-black text-xs uppercase tracking-widest">Highlights</div>
        </div>
        <button
          onClick={clipThis}
          disabled={busy}
          data-testid="clip-this-button"
          className="btn-pill btn-primary text-[10px] px-2 py-1 disabled:opacity-50"
        >
          <FilmSlateIcon size={10} weight="fill" /> Clip this
        </button>
      </div>
      {items.length === 0 && (
        <div className="text-[11px] text-white/30 py-3 text-center">No highlights yet. We mark bursts automatically.</div>
      )}
      <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
        {items.slice(0, 5).map((h, i) => {
          const style = kindColor(h.kind);
          return (
            <div key={i} className="flex items-center gap-2 text-[11px] bg-[#05050A] border border-[#222233] rounded px-2 py-1.5">
              <span style={{ color: style.c }}>{style.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold uppercase tracking-wider" style={{ color: style.c }}>{h.kind}</span>
                  <span className="text-white/40 text-[10px]">{timeAgo(h.created_at)}</span>
                </div>
                {h.sample_text && (
                  <div className="text-white/60 truncate">{h.sample_text}</div>
                )}
              </div>
              <div className="text-[10px] text-white/40">{h.message_count || 0} msgs</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HighlightsFeed;
